import {
  BATTLE_DT,
  INPUT_DELAY_TICKS,
  applyTickBundle,
  DEFAULT_ARENA_SKILL,
  arenaOpponentInput,
  createBattleAiMemory,
  stepBattle,
  type ArenaAiSkill,
  type BattleAiMemory,
  type BattleContext,
  type BattleEvent,
  type BattleResult,
  type BattleState,
  type FighterId,
  type FighterInput,
  type Vec3,
  DUEL_SEATS,
  fighterOf,
  forfeitBattle,
  normalizeAngle,
} from '@archon3d/core';
import type { LockstepLink } from '../../game/lockstepLink';

export interface FighterPose {
  pos: Vec3;
  yaw: number;
  pitch: number;
}

export interface BattleControllerOptions {
  ctx: BattleContext;
  state: BattleState;
  /**
   * The sides people at this device control: one, or both when two people with a controller
   * each meet; the AI drives every other side. Empty when both sides are AI.
   */
  humans: readonly FighterId[];
  humanFriendlyAi?: boolean;
  seed: number;
  /** An online duel: steps come from the server's bundles instead of the local AI and clock. */
  lockstep?: LockstepLink;
  /** How well the AI fighters play; the default is a fair opponent. */
  skill?: ArenaAiSkill;
  /** A skill per side, for seats with their own setting; `skill` covers the rest. */
  skills?: Partial<Record<FighterId, ArenaAiSkill>>;
}

/** What the people at the device want: a reader per side, or one reader for the first person. */
export type HumanInputs = (() => FighterInput) | Partial<Record<FighterId, () => FighterInput>>;

/** The seats a v1 duel fights; the state carries whatever seats its arena gave it. */
export const FIGHTER_IDS: readonly FighterId[] = DUEL_SEATS;
/** A hitch longer than this is dropped rather than simulated in a burst. */
const MAX_FRAME_SECONDS = 0.25;
const MAX_STEPS_PER_FRAME = 8;
/** Bundles kept in hand in a lockstep duel so network jitter does not stall every other frame. */
const LOCKSTEP_SLACK = 1;
/** While no bundle arrives, the local input still goes out this often (it also says "ready"). */
const INPUT_KEEPALIVE_SECONDS = 0.1;

/**
 * Runs the deterministic duel at its fixed rate from a variable frame rate. The poses of the
 * previous step are kept so the view can interpolate between steps instead of showing 30 Hz.
 */
export class BattleController {
  readonly ctx: BattleContext;
  readonly state: BattleState;
  readonly humans: readonly FighterId[];
  /** The first person's side: whose view the HUD takes, and who an online duel sends for. */
  readonly human: FighterId | null;
  private readonly lockstep: LockstepLink | null;
  /** Lockstep: bundles applied so far, which is also the next tick's number. */
  private applied = 0;
  private sinceSend = Infinity;
  private readonly skill: ArenaAiSkill;
  private readonly humanFriendlyAi: boolean;
  private readonly skills: Partial<Record<FighterId, ArenaAiSkill>>;
  private readonly memories = new Map<FighterId, BattleAiMemory>();
  private accumulator = 0;
  private prev: Map<FighterId, FighterPose>;
  private curr: Map<FighterId, FighterPose>;
  private prevProjectiles = new Map<number, Vec3>();
  private currProjectiles: Map<number, Vec3>;
  private pendingEvents: BattleEvent[] = [];

  constructor(options: BattleControllerOptions) {
    this.ctx = options.ctx;
    this.state = options.state;
    this.humans = [...options.humans];
    this.human = this.humans[0] ?? null;
    this.humanFriendlyAi = options.humanFriendlyAi ?? true;
    this.lockstep = options.lockstep ?? null;
    this.skill = { ...(options.skill ?? DEFAULT_ARENA_SKILL) };
    this.skills = { ...options.skills };
    this.state.seats.forEach((id, i) => {
      if (!this.humans.includes(id) && !this.lockstep)
        this.memories.set(id, createBattleAiMemory(options.seed + i + 1));
    });
    this.prev = this.snapshotPoses();
    this.curr = this.snapshotPoses();
    this.currProjectiles = this.snapshotProjectiles();
  }

  get result(): BattleResult | null {
    return this.state.result;
  }

  /**
   * Advances the simulation by one frame; returns the events raised by the steps taken. The
   * people's inputs come per side, or as one function for the first person's side.
   */
  advance(dtSeconds: number, input?: HumanInputs): BattleEvent[] {
    const events = this.pendingEvents;
    this.pendingEvents = [];
    if (this.state.result) return events;
    const humanInputs = this.inputsBySide(input);
    if (this.lockstep)
      return this.advanceLockstep(this.lockstep, dtSeconds, events, humanInputs[this.human!]);
    this.accumulator += Math.min(dtSeconds, MAX_FRAME_SECONDS);
    let steps = 0;
    while (this.accumulator >= BATTLE_DT && steps < MAX_STEPS_PER_FRAME) {
      this.prev = this.curr;
      this.prevProjectiles = this.currProjectiles;
      const inputs: Partial<Record<FighterId, FighterInput>> = {};
      for (const id of this.state.seats) {
        const memory = this.memories.get(id);
        if (memory)
          inputs[id] = arenaOpponentInput(
            this.ctx,
            this.state,
            id,
            memory,
            BATTLE_DT,
            this.skills[id] ?? this.skill,
            this.humans.length > 0 && !this.humans.includes(id),
            this.humanFriendlyAi,
          );
        else {
          const read = humanInputs[id];
          if (read) inputs[id] = read();
        }
      }
      stepBattle(this.ctx, this.state, inputs);
      events.push(...this.state.events);
      this.curr = this.snapshotPoses();
      this.currProjectiles = this.snapshotProjectiles();
      this.accumulator -= BATTLE_DT;
      steps += 1;
      if (this.state.result) break;
    }
    if (steps === MAX_STEPS_PER_FRAME) this.accumulator = 0;
    if (this.state.result) {
      this.accumulator = 0;
      this.prev = this.curr;
      this.prevProjectiles = this.currProjectiles;
    }
    return events;
  }

  /** Where a fighter is drawn this frame: between the previous and the current step. */
  pose(id: FighterId): FighterPose {
    const a = poseOf(this.prev, id);
    const b = poseOf(this.curr, id);
    const t = this.alpha;
    return {
      pos: lerpVec(a.pos, b.pos, t),
      yaw: lerpAngle(a.yaw, b.yaw, t),
      pitch: a.pitch + (b.pitch - a.pitch) * t,
    };
  }

  projectiles(): { id: number; pos: Vec3 }[] {
    const t = this.alpha;
    const out: { id: number; pos: Vec3 }[] = [];
    for (const [id, pos] of this.currProjectiles) {
      const before = this.prevProjectiles.get(id);
      out.push({ id, pos: before ? lerpVec(before, pos, t) : pos });
    }
    return out;
  }

  /**
   * Lockstep: the frame's share of steps comes from the server's bundles, at the fixed rate
   * while they flow, faster when a backlog has built up, and not at all while one is missing.
   * The local input is sent once per step (and at a trickle while waiting) for a tick a little
   * ahead of the one the server is about to simulate.
   */
  private advanceLockstep(
    link: LockstepLink,
    dtSeconds: number,
    events: BattleEvent[],
    humanInput?: () => FighterInput,
  ): BattleEvent[] {
    this.accumulator += Math.min(dtSeconds, MAX_FRAME_SECONDS);
    let allowed = Math.floor(this.accumulator / BATTLE_DT + 1e-6);
    const backlog = link.available;
    if (backlog > LOCKSTEP_SLACK + 1) allowed = Math.max(allowed, backlog - LOCKSTEP_SLACK);
    let steps = 0;
    while (steps < allowed && steps < MAX_STEPS_PER_FRAME) {
      const bundle = link.take();
      if (!bundle) break;
      this.prev = this.curr;
      this.prevProjectiles = this.currProjectiles;
      applyTickBundle(this.ctx, this.state, bundle);
      this.applied += 1;
      events.push(...this.state.events);
      this.curr = this.snapshotPoses();
      this.currProjectiles = this.snapshotProjectiles();
      steps += 1;
      if (this.state.result) break;
    }
    this.accumulator =
      steps > 0
        ? Math.min(BATTLE_DT, Math.max(0, this.accumulator - steps * BATTLE_DT))
        : Math.min(this.accumulator, BATTLE_DT);
    this.sinceSend += dtSeconds;
    if (humanInput && this.human && (steps > 0 || this.sinceSend >= INPUT_KEEPALIVE_SECONDS)) {
      this.sinceSend = 0;
      link.send(this.applied + INPUT_DELAY_TICKS, humanInput());
    }
    if (this.state.result) {
      this.accumulator = 0;
      this.prev = this.curr;
      this.prevProjectiles = this.currProjectiles;
    }
    return events;
  }

  /** A person gives up (the first person's side unless told), which counts as a death. */
  forfeit(side: FighterId | null = this.human): void {
    if (!side || !this.humans.includes(side) || this.state.result) return;
    if (this.lockstep) {
      // The server settles it in a coming bundle, so every client sees the same ending.
      this.lockstep.forfeit();
      return;
    }
    // The core settles a forfeit, so an offline duel ends exactly as a served one does, and
    // reports it as a served one does: the blast, then the death. They are taken off the state
    // here, so a step that stops on the result (a pad's forfeit arrives mid-step) cannot report
    // them a second time.
    forfeitBattle(this.state, side);
    this.pendingEvents.push(...this.state.events);
    this.state.events = [];
  }

  private inputsBySide(input?: HumanInputs): Partial<Record<FighterId, () => FighterInput>> {
    if (!input) return {};
    if (typeof input === 'function') return this.human ? { [this.human]: input } : {};
    return input;
  }

  private get alpha(): number {
    return Math.min(1, this.accumulator / BATTLE_DT);
  }

  private snapshotPoses(): Map<FighterId, FighterPose> {
    return new Map(
      this.state.seats.map((id) => {
        const f = fighterOf(this.state, id);
        return [id, { pos: { ...f.pos }, yaw: f.yaw, pitch: f.pitch }];
      }),
    );
  }

  private snapshotProjectiles(): Map<number, Vec3> {
    return new Map(this.state.projectiles.map((p) => [p.id, { ...p.pos }]));
  }
}

/** A seat's pose from a snapshot; a missing seat is always a bug, never a frame to skip. */
function poseOf(poses: Map<FighterId, FighterPose>, id: FighterId): FighterPose {
  const pose = poses.get(id);
  if (!pose) throw new Error(`No pose for fighter '${id}'`);
  return pose;
}

function lerpVec(a: Vec3, b: Vec3, t: number): Vec3 {
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t, z: a.z + (b.z - a.z) * t };
}

/** Along the shorter way round; the core's fold, so no yaw can stall a frame. */
export function lerpAngle(a: number, b: number, t: number): number {
  return a + normalizeAngle(b - a) * t;
}
